import React, { useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { Shield, LockKeyhole, ArrowRight, AlertTriangle } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { http } from "@/lib/api";

/**
 * Super Admin login — deliberately separate from the tenant Login page.
 *
 * Why a distinct route?
 *   - Tenant users should never see a UI that hints the superadmin surface
 *     exists. Keeping the path out of the tenant sidebar and splitting the
 *     login URL reduces shoulder-surfing risk and avoids confusing regular
 *     customers with "Super Admin" chrome in their normal sign-in flow.
 *
 * Authorization gate:
 *   - We call /api/auth/login directly (same endpoint, same brute-force
 *     throttling, same suspension gating) WITHOUT going through
 *     AuthContext.login() — that way a tenant admin's credentials never
 *     populate the in-memory user state, so the render guard won't
 *     yank them to "/" before the inline error renders.
 *   - If role === superadmin → call AuthContext.reload() to hydrate the
 *     context, then navigate to /superadmin.
 *   - If role !== superadmin → POST /auth/logout to drop the cookie the
 *     server just set, show a clear rejection, stay on this page.
 */
export default function SuperAdminLogin() {
    const { user, reload } = useAuth();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [err, setErr] = useState("");
    const [loading, setLoading] = useState(false);
    const location = useLocation();
    const navigate = useNavigate();

    // Already signed in and already a superadmin → deep-link straight into the
    // dashboard (or back to the page they tried to visit). Regular tenant
    // users who somehow land here get redirected out so they don't see the
    // platform-owner chrome.
    if (user && user.role === "superadmin") {
        return <Navigate to={location.state?.from || "/superadmin"} replace />;
    }
    if (user && user.role !== "superadmin") {
        return <Navigate to="/" replace />;
    }

    const submit = async (e) => {
        e.preventDefault();
        setErr("");
        setLoading(true);
        try {
            const { data } = await http.post("/auth/login", { email, password });
            if (data?.role !== "superadmin") {
                // Drop the server-side session we just opened — this URL is
                // only for platform owners. We intentionally skipped
                // AuthContext.login() above, so the client-side `user` state
                // is still null and no tenant UI flashed on screen.
                try { await http.post("/auth/logout"); } catch (e) {
                    // Best-effort logout of the unauthorised tenant
                    // session. If the call itself fails the user is
                    // already shown a "not authorised" message below;
                    // we just need DevTools visibility for debugging.
                    console.warn("superadmin-login: tenant-session logout failed:", e?.message || e);
                }
                setErr(
                    "This account is not authorized for the Super Admin console. " +
                    "Use the tenant sign-in page at /login instead.",
                );
                setLoading(false);
                return;
            }
            // Hydrate the context from the cookie the server just set so
            // SuperAdminRoute sees user.role === "superadmin" on the next
            // render and doesn't bounce us back to this page.
            await reload();
            navigate(location.state?.from || "/superadmin", { replace: true });
        } catch (e) {
            setErr(
                e?.response?.data?.detail ||
                e?.message ||
                "Unable to sign in — check your credentials.",
            );
            setLoading(false);
        }
    };

    return (
        <div
            className="relative flex min-h-screen items-center justify-center overflow-hidden px-6 text-white"
            data-testid="superadmin-login-page"
            style={{
                fontFamily: "Manrope, 'Inter var', Inter, system-ui, sans-serif",
                background:
                    "radial-gradient(1200px 600px at 10% -10%, rgba(0,74,173,0.22), transparent 60%), " +
                    "radial-gradient(900px 500px at 90% -20%, rgba(0,194,255,0.14), transparent 55%), #050b1c",
            }}
        >
            {/* Decorative grid */}
            <div
                className="pointer-events-none absolute inset-0 opacity-[0.05]"
                style={{
                    backgroundImage:
                        "linear-gradient(rgba(255,255,255,0.4) 1px, transparent 1px), " +
                        "linear-gradient(90deg, rgba(255,255,255,0.4) 1px, transparent 1px)",
                    backgroundSize: "40px 40px",
                }}
            />

            <div
                className="relative w-full max-w-md rounded-2xl border border-white/[0.06] bg-white/[0.02] p-8 shadow-[0_40px_80px_-40px_rgba(0,0,0,0.9)] backdrop-blur-xl"
                data-testid="sa-login-card"
            >
                <div className="flex items-center gap-3">
                    <div
                        className="flex h-10 w-10 items-center justify-center rounded-lg"
                        style={{ background: "linear-gradient(135deg, #004AAD 0%, #00C2FF 100%)" }}
                    >
                        <Shield size={18} strokeWidth={2.5} />
                    </div>
                    <div className="leading-tight">
                        <div className="text-[15px] font-semibold tracking-tight">MoovBytes</div>
                        <div className="text-[9.5px] uppercase tracking-[0.24em] text-[#00C2FF]/90">
                            Super Admin Console
                        </div>
                    </div>
                </div>

                <h1 className="mt-8 text-[22px] font-semibold tracking-tight">
                    Platform owner sign-in
                </h1>
                <p className="mt-1.5 text-[12.5px] leading-relaxed text-white/55">
                    This page is restricted to platform owners. Tenant users should use the{" "}
                    <a
                        href="/login"
                        className="text-[#00C2FF] underline-offset-2 hover:underline"
                        data-testid="sa-login-tenant-link"
                    >
                        standard workspace sign-in
                    </a>
                    .
                </p>

                <form onSubmit={submit} className="mt-6 space-y-4">
                    <label className="block">
                        <span className="mb-1.5 block text-[10px] uppercase tracking-wider text-white/45">
                            Email
                        </span>
                        <input
                            data-testid="sa-login-email"
                            type="email"
                            required
                            autoFocus
                            autoComplete="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="you@moovbytes.com"
                            className="h-10 w-full rounded-md border border-white/10 bg-white/[0.03] px-3 text-[13px] text-white placeholder:text-white/25 focus:border-[#00C2FF]/50 focus:outline-none focus:ring-1 focus:ring-[#00C2FF]/30"
                        />
                    </label>

                    <label className="block">
                        <span className="mb-1.5 block text-[10px] uppercase tracking-wider text-white/45">
                            Password
                        </span>
                        <div className="relative">
                            <LockKeyhole
                                size={13}
                                className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-white/35"
                            />
                            <input
                                data-testid="sa-login-password"
                                type="password"
                                required
                                autoComplete="current-password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                placeholder="••••••••"
                                className="h-10 w-full rounded-md border border-white/10 bg-white/[0.03] pl-8 pr-3 text-[13px] text-white placeholder:text-white/25 focus:border-[#00C2FF]/50 focus:outline-none focus:ring-1 focus:ring-[#00C2FF]/30"
                            />
                        </div>
                    </label>

                    {err && (
                        <div
                            data-testid="sa-login-error"
                            className="flex items-start gap-2 rounded-md border border-red-400/30 bg-red-400/5 p-2.5 text-[11.5px] text-red-200"
                        >
                            <AlertTriangle size={12} className="mt-0.5 shrink-0" />
                            <span>{err}</span>
                        </div>
                    )}

                    <button
                        data-testid="sa-login-submit"
                        type="submit"
                        disabled={loading}
                        className="flex h-10 w-full items-center justify-center gap-2 rounded-md text-[13px] font-semibold text-white transition-transform disabled:opacity-50"
                        style={{
                            background: "linear-gradient(135deg, #004AAD 0%, #00C2FF 100%)",
                            boxShadow: "0 10px 24px -12px rgba(0,194,255,0.5)",
                        }}
                    >
                        {loading ? "Signing in…" : "Enter console"}
                        {!loading && <ArrowRight size={14} />}
                    </button>
                </form>

                <div className="mt-6 border-t border-white/[0.06] pt-4 text-[10.5px] uppercase tracking-wider text-white/35">
                    Protected · audit-logged · platform owner only
                </div>
            </div>
        </div>
    );
}
